import axios from 'axios';
import { AutocompleteResult, LoadOptionsAsyncHandler } from 'react-select';
import { computed } from 'mobx';
import { LocalStorage } from '../domain/LocalStorage';
import { SchoolOption } from '../domain/SchoolOption';
import { SchoolQueryResponse } from '../domain/SchoolQueryResponse';
import { EMPTY, NOT_FOUND, SchoolsAutocompleteResult, TOO_MANY } from './appServiceResult';
import { cookieEnabled } from '../config';
import environmentCore from '../config/environment';

const LocalStorageMock: LocalStorage = {
  getItem: (index: string): any => [],
  setItem: (index: string, item: any): void => {
  },
};

let localStorage: LocalStorage = LocalStorageMock;

if (cookieEnabled) {
  try {
    localStorage = require('mobx-localstorage');
  } catch (e) {
    localStorage = LocalStorageMock;
  }
}

interface IEnvironment {
  SCHOOLQUERY_BACKEND_URL: string;
  SCHOOLQUERY_REQUEST_PREFIX: string;
  SCHOOLQUERY_DEBOUNCE_THRESHOLD_MS: number;
  MAX_SCHOOLS: number;
  UNTIS_URL: string;
}

export const getEnvironment = (): IEnvironment => {
  return { ...environmentCore };
};

/**
 * Service zur Abfrage der Schulen vom Server sowie der Speicherung der zuletzt gesuchten Schulen im LocalStorage
 */
export class AppService {
  private environment: IEnvironment = getEnvironment();
  private lastResult: AutocompleteResult<number> = EMPTY;
  private lastResultTimestamp: number = 0;
  private oldTimeout: NodeJS.Timer;

  constructor() {
    this.init();
  }

  /**
   * Fetched die config für das aktuelle environment
   */
  async init(): Promise<void> {
    let override;
    try {
      override = (await axios.get('/environment.json').then(({ data }) => data)) || {};
      if (typeof override !== 'object') {
        override = {};
      }
    } catch (error) {
      override = {};
    }

    this.environment = {
      ...this.environment,
      ...override,
    };
  }

  /**
   * Abfrage der zuletzt besuchten Schulen
   *
   * @returns {SchoolOption[]}
   */
  @computed
  public get visitedSchools(): SchoolOption[] {
    let visitedSchoolsJSON = localStorage.getItem('visitedSchools');
    // return visitedSchoolsJSON ? JSON.parse(visitedSchoolsJSON || "") : [];
    return visitedSchoolsJSON || [];
  }

  /**
   * Speichern der zuletzt besuchten Schulen
   *
   * @param {SchoolOption[]} schools
   */
  public set visitedSchools(schools: SchoolOption[]) {
    // localStorage.setItem("visitedSchools", JSON.stringify(schools));
    localStorage.setItem('visitedSchools', schools);
  }

  /**
   * Abfrage der Schulen vom Server
   *
   * @param {string} searchTerm aktuell eingetippter Suchbegriff
   * @returns {Promise<AutocompleteResult<number>>}
   */
  public getSchools: LoadOptionsAsyncHandler<number> = (searchTerm: string): Promise<AutocompleteResult<number>> => {
    const {
      SCHOOLQUERY_DEBOUNCE_THRESHOLD_MS,
      SCHOOLQUERY_BACKEND_URL,
      SCHOOLQUERY_REQUEST_PREFIX,
      UNTIS_URL,
    } = this.environment;

    // wenn der search term weniger als 3 characters hat macht ein
    // request keinen sinn, da liefert das backend immer "too many results"
    if (searchTerm.length < 3) {
      return Promise.resolve(EMPTY);
    }
    // selbstgestricktes debouncing: die verwendung von debounce() machte wohl probleme...
    let diffToPreviousRequest: number = getTimestamp() - this.lastResultTimestamp;
    if (diffToPreviousRequest < SCHOOLQUERY_DEBOUNCE_THRESHOLD_MS) {
      // Send last searchTerm to the Server.
      return new Promise((resolve) => {
        // Delete (clear oldTimeout) all previous request because the are not the final search term.
        clearTimeout(this.oldTimeout);
        this.oldTimeout = setTimeout(() => {
          resolve(this.getSchools(searchTerm));
          // minimal: SCHOOLQUERY_DEBOUNCE_THRESHOLD_MS - diffToPreviousRequest + 1,
          // so that in any case a request will send as soon as possible.
          // (@see SCHOOLQUERY_DEBOUNCE_THRESHOLD_MS)
        }, SCHOOLQUERY_DEBOUNCE_THRESHOLD_MS - diffToPreviousRequest + 1);
      });
    }

    // in der then() clause von promises ist "this" nicht mehr viel wert...
    let thisSaved: AppService = this;
    /**
     * beispiel request post body:
     * {"id":"schulsuche","method":"searchSchool","params":[{"search":"korneuburg"}],"jsonrpc":"2.0"}
     *
     * beispiel responses:
     * {"id":"schulsuche","error":{"code":-6003,"message":"too many results"},"jsonrpc":"2.0"}
     *
     * {"result":{"size":0,"schools":[{"server":"thalia.webuntis.com","useMobileServiceUrlAndroid":true,
     * "address":"2100 Korneuburg, Liese Prokop-Str. 1","displayName":"BG und BRG Korneuburg",
     * "loginName":"AHS-Korneuburg","schoolId":5778600,"useMobileServiceUrlIos":false,
     * "mobileServiceUrl":"https:\/\/mobile.webuntis.com\/ms\/app\/5778600"},{"server":
     * "thalia.webuntis.com","useMobileServiceUrlAndroid":true,"address":"2100 Korneuburg, Bankmannring 1",
     * "displayName":"BHAK Korneuburg","loginName":"hak-korneuburg","schoolId":7012500,
     * "useMobileServiceUrlIos":false,"mobileServiceUrl":"https:\/\/mobile.webuntis.com\/ms\/app\/7012500"}]},
     * "id":"schulsuche-TODO","jsonrpc":"2.0"}
     */
    return axios({
      method: 'POST',
      url: SCHOOLQUERY_BACKEND_URL,
      data: {
        id: SCHOOLQUERY_REQUEST_PREFIX + getTimestamp(),
        method: 'searchSchool',
        params: [{ search: searchTerm }],
        jsonrpc: '2.0',
      },
    })
      .then(function(response: SchoolQueryResponse) {
        if (response.data.error) {
          switch (response.data.error.code) {
            case -6003:
              return TOO_MANY;
            default:
              console.error(
                '[code ' +
                response.data.error.code +
                '] while querying for \'' +
                searchTerm +
                '\': ' +
                response.data.error.message,
              );
              break;
          }
        } else if (response.data.result) {
          const schools = response.data.result.schools;
          thisSaved.lastResult =
            schools.length > 0 ? new SchoolsAutocompleteResult(schools, searchTerm) : NOT_FOUND(UNTIS_URL);
          thisSaved.lastResultTimestamp = getTimestamp();
          return thisSaved.lastResult;
        }
        return NOT_FOUND(UNTIS_URL);
      })
      .catch(function(error: any) {
        console.error('while querying for \'' + searchTerm + '\': ' + error);
        return EMPTY;
      });
  }

  /**
   * Auswählen einer Schule
   *
   * @param {SchoolOption} school
   */
  public selectSchool = (school: SchoolOption): void => {
    let visitedSchools = this.visitedSchools;
    let schoolExists: boolean = !!visitedSchools.find((s: SchoolOption) => s.id == school.id);

    if (!schoolExists && school.id >= 0) {
      visitedSchools.unshift(school);
    }

    this.visitedSchools = visitedSchools.slice(0, this.environment.MAX_SCHOOLS);
    window.location.href = school.url || '#';
  }

  /**
   * Löschen einer Schule aus den "Favourites"
   *
   * @param {number} id
   */
  public deleteSchool = (id: number): void => {
    let visitedSchools = this.visitedSchools;
    visitedSchools = visitedSchools.filter((s: SchoolOption) => s.id != id);
    this.visitedSchools = visitedSchools;
  }
}

const getTimestamp = (): number => {
  return new Date().getTime();
};

export const appService = new AppService();



// WEBPACK FOOTER //
// ./src/app/appService.ts